import React, { useState, useCallback, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { 
  Home, 
  Calendar, 
  Building2, 
  Users, 
  BarChart2, 
  LogOut,
  Menu,
  X,
  ChevronDown,
  Globe,
  Sun,
  Moon,
  Settings
} from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import UserProfileModal from './UserProfileModal';

const languages = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'it', label: 'Italiano' }
];

const menuItems = [
  { path: '/', icon: Home, label: 'dashboard.title', roles: ['owner', 'admin', 'super_admin'] },
  { path: '/bookings', icon: Calendar, label: 'bookings.title', roles: ['support', 'owner', 'admin', 'super_admin'] },
  { path: '/properties', icon: Building2, label: 'properties.title', roles: ['admin', 'super_admin'] },
  { path: '/users', icon: Users, label: 'users.title', roles: ['admin', 'super_admin'] },
  { path: '/statistics', icon: BarChart2, label: 'dashboard.stats.title', roles: ['admin', 'super_admin'] },
];

export default function Layout() {
  const { user, userRole, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return document.documentElement.classList.contains('dark');
  });
  const [showProfileModal, setShowProfileModal] = useState(false);

  const filteredMenuItems = menuItems.filter(item => 
    userRole && item.roles.includes(userRole)
  );

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, [signOut]);

  const toggleDarkMode = useCallback(() => {
    setIsDarkMode(prev => {
      const newValue = !prev;
      document.documentElement.classList.toggle('dark');
      localStorage.setItem('darkMode', String(newValue));
      return newValue;
    });
  }, []);

  const changeLanguage = useCallback((lang: string) => {
    i18n.changeLanguage(lang);
    setIsLanguageMenuOpen(false);
    localStorage.setItem('language', lang);
  }, [i18n]);

  const handleNavigation = useCallback((path: string) => {
    navigate(path);
    setIsSidebarOpen(false);
  }, [navigate]);

  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    const savedLanguage = localStorage.getItem('language');

    if (savedDarkMode !== isDarkMode) {
      setIsDarkMode(savedDarkMode);
      document.documentElement.classList.toggle('dark', savedDarkMode);
    }

    if (savedLanguage && savedLanguage !== i18n.language) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n, isDarkMode]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.language-menu')) {
        setIsLanguageMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fermer le menu quand la route change
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user || !userRole) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="lg:hidden fixed top-4 right-4 z-50">
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="p-2 rounded-lg bg-white dark:bg-gray-800 shadow-lg"
          aria-label={isSidebarOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
        >
          {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <div className="flex h-screen">
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-40
          w-64 bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-200 ease-in-out
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-center h-16 border-b dark:border-gray-700">
              <h1 className="text-xl font-bold text-gray-800 dark:text-white">
                {t('common.welcome')}
              </h1>
            </div>

            <nav className="flex-1 overflow-y-auto py-4">
              <ul className="space-y-1 px-3">
                {filteredMenuItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <li key={item.path}>
                      <button
                        onClick={() => handleNavigation(item.path)}
                        className={`
                          w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors duration-150
                          ${isActive 
                            ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400' 
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'}
                        `}
                      >
                        <item.icon size={20} />
                        <span>{t(item.label)}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <div className="p-4 border-t dark:border-gray-700 space-y-4">
              <button
                onClick={() => setShowProfileModal(true)}
                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg"
              >
                <Settings size={20} />
                <span>{t('common.settings')}</span>
              </button>

              <div className="relative language-menu">
                <button
                  onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                  className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <Globe size={20} />
                    <span>{languages.find(lang => lang.code === i18n.language)?.label}</span>
                  </div>
                  <ChevronDown 
                    size={16} 
                    className={`transform transition-transform ${isLanguageMenuOpen ? 'rotate-180' : ''}`} 
                  />
                </button>

                {isLanguageMenuOpen && (
                  <div className="absolute bottom-full left-0 w-full mb-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={toggleDarkMode}
                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg"
              >
                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
                <span>{isDarkMode ? t('common.lightMode') : t('common.darkMode')}</span>
              </button>

              <button
                onClick={handleSignOut}
                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/50 rounded-lg"
              >
                <LogOut size={20} />
                <span>{t('common.logout')}</span>
              </button>
            </div>
          </div>
        </aside>

        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>

      {showProfileModal && (
        <UserProfileModal onClose={() => setShowProfileModal(false)} />
      )}
    </div>
  );
}